import type { ScheduledRunWithTemplate } from "@/types/api/ScheduledRunRecord"
import { getTimezoneAbbreviation } from "@/utils/date/timezone"

type ScheduledRunsStatusFilter = "ALL" | "ENABLED" | "DISABLED"
const NUMERIC_CRON_TOKEN_REGEX = /^\d+$/
const MIN_CRON_HOUR = 0
const MAX_CRON_HOUR = 23
const MIN_CRON_MINUTE = 0
const MAX_CRON_MINUTE = 59

interface FilterScheduledRunsParams {
  agentFilter: string | null
  runs: ScheduledRunWithTemplate[] | null | undefined
  searchValue: string
  statusFilter: ScheduledRunsStatusFilter
}

interface ScheduledRunsEmptyStateParams {
  agentFilter: string | null
  isInitialLoading: boolean
  searchValue: string
  statusFilter: ScheduledRunsStatusFilter
}

export function filterScheduledRuns({
  agentFilter,
  runs,
  searchValue,
  statusFilter,
}: FilterScheduledRunsParams): ScheduledRunWithTemplate[] {
  if (!runs) {
    return []
  }

  let filteredRuns = runs

  if (agentFilter) {
    filteredRuns = filteredRuns.filter(
      (run) => run.template_agent_id === agentFilter,
    )
  }

  if (statusFilter !== "ALL") {
    const isEnabled = statusFilter === "ENABLED"
    filteredRuns = filteredRuns.filter((run) => run.enabled === isEnabled)
  }

  const normalizedSearch = searchValue.trim().toLowerCase()
  if (!normalizedSearch) {
    return filteredRuns
  }

  return filteredRuns.filter(
    (run) =>
      run.display_name.toLowerCase().includes(normalizedSearch) ||
      run.id.toLowerCase().includes(normalizedSearch) ||
      run.template_display_name.toLowerCase().includes(normalizedSearch) ||
      run.template_agent_id?.toLowerCase().includes(normalizedSearch),
  )
}

export function hasScheduledRunsFilters({
  agentFilter,
  searchValue,
  statusFilter,
}: Omit<ScheduledRunsEmptyStateParams, "isInitialLoading">): boolean {
  return Boolean(statusFilter !== "ALL" || agentFilter || searchValue.trim())
}

export function shouldShowScheduledRunsEmptyCreateAction({
  agentFilter,
  isInitialLoading,
  searchValue,
  statusFilter,
}: ScheduledRunsEmptyStateParams): boolean {
  if (isInitialLoading) {
    return false
  }

  return !hasScheduledRunsFilters({
    agentFilter,
    searchValue,
    statusFilter,
  })
}

function convertHourTo12(hour24: number): number {
  if (hour24 === 0) {
    return 12
  }

  if (hour24 > 12) {
    return hour24 - 12
  }

  return hour24
}

function isValidCronHour(hour24: number): boolean {
  return hour24 >= MIN_CRON_HOUR && hour24 <= MAX_CRON_HOUR
}

function isValidCronMinute(minute: number): boolean {
  return minute >= MIN_CRON_MINUTE && minute <= MAX_CRON_MINUTE
}

export function formatScheduleExpression(
  expression: string,
  timezone?: string,
): string {
  if (expression.startsWith("rate(")) {
    const match = expression.match(/rate\((\d+)\s+(\w+)\)/)
    if (match) {
      const value = parseInt(match[1], 10)
      const unit = match[2].replace(/s$/, "")

      if (unit === "hour" && value === 1) {
        return "Every hour"
      }

      if (
        (unit === "hour" && value === 24) ||
        (unit === "day" && value === 1)
      ) {
        return "Daily"
      }

      if (unit === "day" && value === 7) {
        return "Weekly"
      }

      return `Every ${value} ${value === 1 ? unit : `${unit}s`}`
    }
  }

  if (expression.startsWith("cron(")) {
    const match = expression.match(/cron\((.+)\)/)
    if (match) {
      const parts = match[1].split(" ")
      if (parts.length >= 5) {
        const minute = parts[0]
        const hour = parts[1]
        const dayOfWeek = parts[4]

        if (
          !NUMERIC_CRON_TOKEN_REGEX.test(hour) ||
          !NUMERIC_CRON_TOKEN_REGEX.test(minute)
        ) {
          return expression
        }

        const hourNum = parseInt(hour, 10)
        const minuteNum = parseInt(minute, 10)

        if (!isValidCronHour(hourNum) || !isValidCronMinute(minuteNum)) {
          return expression
        }

        const amPm = hourNum >= 12 ? "PM" : "AM"
        const hour12 = convertHourTo12(hourNum)
        const timeString = `${hour12}:${minuteNum.toString().padStart(2, "0")} ${amPm}`
        const tzSuffix = timezone ? ` ${getTimezoneAbbreviation(timezone)}` : ""

        if (dayOfWeek === "?" || dayOfWeek === "*") {
          return `Daily at ${timeString}${tzSuffix}`
        }

        const dayMap: Record<string, string> = {
          SUN: "Sun",
          MON: "Mon",
          TUE: "Tue",
          WED: "Wed",
          THU: "Thu",
          FRI: "Fri",
          SAT: "Sat",
        }

        const days = dayOfWeek.split(",").map((day) => dayMap[day] || day)

        if (
          days.length === 5 &&
          !days.includes("Sat") &&
          !days.includes("Sun")
        ) {
          return `Weekdays at ${timeString}${tzSuffix}`
        }

        if (days.length === 2 && days.includes("Sat") && days.includes("Sun")) {
          return `Weekends at ${timeString}${tzSuffix}`
        }

        return `${days.join(", ")} at ${timeString}${tzSuffix}`
      }
    }
  }

  return expression
}
